import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../supabaseClient';

/**
 * 產生 6 碼 session code（排除容易混淆的字元 I, O, 0, 1）
 */
const generateSessionCode = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars[Math.floor(Math.random() * chars.length)];
  }
  return code;
};

/**
 * DailyReport 簡報模式 Hook
 *
 * 使用 Supabase Realtime 的 Broadcast + Presence 功能：
 * - Broadcast: 同步 Presenter 的操作狀態給所有 Viewer
 * - Presence: 追蹤在線參與者
 *
 * @param {Object} options
 * @param {Object} options.profile - 當前使用者的 profile
 * @param {Function} options.onStateReceived - 收到 Presenter 狀態同步時的 callback
 * @param {Function} options.onSessionEnd - Session 被 Host 結束時的 callback
 */
export function useDailyReportBroadcast({ profile, onStateReceived, onDataReceived, onSessionEnd, onPresenterAckMissed, onSubscribeError }) {
  const [isInSession, setIsInSession] = useState(false);
  const [isHost, setIsHost] = useState(false);
  const [isPresenter, setIsPresenter] = useState(false);
  const [sessionCode, setSessionCode] = useState('');
  const [participants, setParticipants] = useState([]);
  const [presenterId, setPresenterId] = useState(null);

  const channelRef = useRef(null);
  const onStateReceivedRef = useRef(onStateReceived);
  const onDataReceivedRef = useRef(onDataReceived);
  const onSessionEndRef = useRef(onSessionEnd);
  const onPresenterAckMissedRef = useRef(onPresenterAckMissed);
  const onSubscribeErrorRef = useRef(onSubscribeError);
  const isPresenterRef = useRef(false);
  const ackTimeoutRef = useRef(null);
  const expectedAckRef = useRef(null);
  // 記錄上次 cleanup 時間：setupChannel 開始時若距離不到 200ms，會 sleep 補齊
  // 給 server 時間清掉舊 socket 訂閱狀態，避免新 subscribe 卡住沒回 SUBSCRIBED。
  const lastCleanupAtRef = useRef(0);

  // 保持 callback refs 最新
  useEffect(() => { onStateReceivedRef.current = onStateReceived; }, [onStateReceived]);
  useEffect(() => { onDataReceivedRef.current = onDataReceived; }, [onDataReceived]);
  useEffect(() => { onSessionEndRef.current = onSessionEnd; }, [onSessionEnd]);
  useEffect(() => { onPresenterAckMissedRef.current = onPresenterAckMissed; }, [onPresenterAckMissed]);
  useEffect(() => { onSubscribeErrorRef.current = onSubscribeError; }, [onSubscribeError]);
  useEffect(() => { isPresenterRef.current = isPresenter; }, [isPresenter]);

  const clearAckTimeout = useCallback(() => {
    if (ackTimeoutRef.current) {
      clearTimeout(ackTimeoutRef.current);
      ackTimeoutRef.current = null;
    }
    expectedAckRef.current = null;
  }, []);

  const cleanup = useCallback(() => {
    clearAckTimeout();
    if (channelRef.current) {
      channelRef.current.untrack();
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
      lastCleanupAtRef.current = Date.now();
    }
    setIsInSession(false);
    setIsHost(false);
    setIsPresenter(false);
    setSessionCode('');
    setParticipants([]);
    setPresenterId(null);
    isPresenterRef.current = false;
  }, [clearAckTimeout]);

  const setupChannel = useCallback((code, asHost) => {
    // 先把 ref 清掉，避免 async 期間其他地方誤用舊 channel。
    const previousChannel = channelRef.current;
    channelRef.current = null;

    const start = async () => {
      // 立即重進時 server 端可能還沒清掉舊 socket 在這個 channel name 上的訂閱狀態，
      // 導致新 channel 收不到 broadcast 或 subscribe 卡住沒回 SUBSCRIBED。
      // 兩條路徑都要等：
      //   (a) setupChannel 內看到 previousChannel — await untrack/remove
      //   (b) 之前已被 cleanup() 清掉 channelRef — 透過 lastCleanupAtRef 補齊距上次清理 200ms
      if (previousChannel) {
        try { await previousChannel.untrack(); } catch { /* ignore */ }
        await supabase.removeChannel(previousChannel);
        lastCleanupAtRef.current = Date.now();
      }
      const elapsed = Date.now() - lastCleanupAtRef.current;
      if (lastCleanupAtRef.current > 0 && elapsed < 200) {
        await new Promise((resolve) => setTimeout(resolve, 200 - elapsed));
      }

      const channel = supabase.channel(`daily-report:${code}`, {
        config: {
          broadcast: { self: false },
          presence: { key: profile.id },
        },
      });

      // 防止 subscribe error 後 cleanup 觸發 'CLOSED' 又把同一個 callback 跑一次。
      let errorHandled = false;

      channel
        .on('broadcast', { event: 'state_sync' }, ({ payload }) => {
          if (!isPresenterRef.current) {
            onStateReceivedRef.current?.(payload);
          }
        })
        .on('broadcast', { event: 'data_sync' }, ({ payload }) => {
          if (!isPresenterRef.current) {
            onDataReceivedRef.current?.(payload);
          }
        })
        .on('broadcast', { event: 'presenter_change' }, ({ payload }) => {
          setPresenterId(payload.presenterId);
          const amIPresenter = payload.presenterId === profile.id;
          setIsPresenter(amIPresenter);
          isPresenterRef.current = amIPresenter;
          // 被指定為 presenter 時立即回送 ack，讓 Host 確認訊息有送達
          if (amIPresenter) {
            channel.send({
              type: 'broadcast',
              event: 'presenter_ack',
              payload: { presenterId: profile.id },
            });
          }
        })
        .on('broadcast', { event: 'presenter_ack' }, ({ payload }) => {
          if (expectedAckRef.current === payload.presenterId) {
            clearAckTimeout();
          }
        })
        .on('broadcast', { event: 'session_end' }, () => {
          onSessionEndRef.current?.();
          cleanup();
        })
        .on('presence', { event: 'sync' }, () => {
          const state = channel.presenceState();
          const users = Object.values(state).flat();
          // 同一個 user 在離開後立即重新加入時，server 端可能短暫保留舊的 presence ref，
          // 造成同一個 id 出現多筆。依 id 去重，保留 joinedAt 最新的那一筆。
          const dedupedMap = new Map();
          for (const u of users) {
            const existing = dedupedMap.get(u.id);
            if (!existing || (u.joinedAt ?? 0) > (existing.joinedAt ?? 0)) {
              dedupedMap.set(u.id, u);
            }
          }
          setParticipants(Array.from(dedupedMap.values()));
        })
        .subscribe(async (status, err) => {
          if (status === 'SUBSCRIBED') {
            await channel.track({
              id: profile.id,
              name: profile.name,
              avatar_url: profile.avatar_url,
              isHost: asHost,
              joinedAt: Date.now(),
            });
            setIsInSession(true);
          } else if (!errorHandled && (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT')) {
            // 'CLOSED' 是正常 unsubscribe / removeChannel 也會跑的狀態，不視為錯誤。
            errorHandled = true;
            console.warn('[useDailyReportBroadcast] subscribe failed:', status, err);
            onSubscribeErrorRef.current?.(status);
            cleanup();
          }
        });

      channelRef.current = channel;
    };

    start();
  }, [profile, cleanup, clearAckTimeout]);

  /**
   * Host 建立簡報 Session
   * @param {string} [fixedCode] - 可傳入固定 code（如部門代碼），不傳則隨機產生
   * @returns {string} session code
   */
  const createSession = useCallback((fixedCode) => {
    const code = fixedCode || generateSessionCode();
    setSessionCode(code);
    setIsHost(true);
    setIsPresenter(true);
    isPresenterRef.current = true;
    setPresenterId(profile.id);
    setupChannel(code, true);
    return code;
  }, [profile, setupChannel]);

  /**
   * 加入已存在的 Session
   * @param {string} code - 6 碼 session code
   * @returns {boolean} 是否成功（格式驗證）
   */
  const joinSession = useCallback((code) => {
    const formattedCode = code.toUpperCase().trim();
    if (formattedCode.length !== 6) return false;

    setSessionCode(formattedCode);
    setIsHost(false);
    setIsPresenter(false);
    isPresenterRef.current = false;
    setupChannel(formattedCode, false);
    return true;
  }, [setupChannel]);

  /**
   * Presenter 廣播 UI 狀態（輕量，頻繁）
   */
  const broadcastState = useCallback((state) => {
    if (!channelRef.current || !isPresenterRef.current) return;
    channelRef.current.send({
      type: 'broadcast',
      event: 'state_sync',
      payload: state,
    });
  }, []);

  /**
   * Presenter 廣播資料（重量，只在切換成員時）
   */
  const broadcastData = useCallback((data) => {
    if (!channelRef.current || !isPresenterRef.current) return;
    channelRef.current.send({
      type: 'broadcast',
      event: 'data_sync',
      payload: data,
    });
  }, []);

  /**
   * Host 指定報告者（Presenter）
   *
   * 指定他人時會啟動 1.5s ack timeout：對方收到 presenter_change 應立即回送 presenter_ack；
   * 若 timeout 內沒收到 ack（對方斷線、ghost ref、訊息路由失敗等），自動把 presenter
   * 還原為 Host 自己，並透過 onPresenterAckMissed 通知 UI。
   */
  const assignPresenter = useCallback((userId) => {
    if (!channelRef.current || !isHost) return;
    clearAckTimeout();
    setPresenterId(userId);
    const amIPresenter = userId === profile.id;
    setIsPresenter(amIPresenter);
    isPresenterRef.current = amIPresenter;
    channelRef.current.send({
      type: 'broadcast',
      event: 'presenter_change',
      payload: { presenterId: userId },
    });
    if (!amIPresenter) {
      expectedAckRef.current = userId;
      ackTimeoutRef.current = setTimeout(() => {
        ackTimeoutRef.current = null;
        if (expectedAckRef.current !== userId) return;
        expectedAckRef.current = null;
        // 對方沒在時間內 ack，把 presenter 還給 Host
        setPresenterId(profile.id);
        setIsPresenter(true);
        isPresenterRef.current = true;
        channelRef.current?.send({
          type: 'broadcast',
          event: 'presenter_change',
          payload: { presenterId: profile.id },
        });
        onPresenterAckMissedRef.current?.(userId);
      }, 1500);
    }
  }, [isHost, profile, clearAckTimeout]);

  /**
   * Host 結束 Session（通知所有人）
   */
  const endSession = useCallback(() => {
    if (!channelRef.current) return;
    channelRef.current.send({
      type: 'broadcast',
      event: 'session_end',
      payload: {},
    });
    cleanup();
  }, [cleanup]);

  /**
   * 非 Host 離開 Session
   */
  const leaveSession = useCallback(() => {
    cleanup();
  }, [cleanup]);

  // Unmount + 關閉分頁時清理 channel
  useEffect(() => {
    const cleanupChannel = () => {
      if (channelRef.current) {
        channelRef.current.untrack();
        supabase.removeChannel(channelRef.current);
      }
    };
    window.addEventListener('beforeunload', cleanupChannel);
    return () => {
      window.removeEventListener('beforeunload', cleanupChannel);
      cleanupChannel();
    };
  }, []);

  return {
    isInSession,
    isHost,
    isPresenter,
    sessionCode,
    participants,
    presenterId,
    createSession,
    joinSession,
    endSession,
    leaveSession,
    assignPresenter,
    broadcastState,
    broadcastData,
  };
}
